import _ from 'lodash';
import PictionJS from 'piction-js';
import queryString from 'query-string';
import { fromJS } from 'immutable';
import appConfig, { updateConfig } from '../appConfig';
import { getKeyPath } from './immutableUtils';
import {
  SESSION_CODE_INVALID_MESSAGE,
  SESSION_EXPIRED_MESSAGE,
} from '../constants/sessionStateTypes';
import {
  registerUserCodeFailure,
  registerUserCodeRequest, registerUserCodeSuccess,
  sessionExpired,
  updateStoreFromNewConfig,
} from '../actions/session';
import { retrieveUMOsFailure } from '../actions/umoGallery';
import { LOCAL_STORAGE_EXT_URL, LOCAL_STORAGE_SURL_KEY, LOCAL_STORAGE_KAUI2CONF } from '../constants/session';
import { embedExternalScript } from './uiUtils';
import { addIntlError, addIntlInfo } from '../actions/messageDialog';

let isWorkbenchValue = false;

const piction = new PictionJS(appConfig.hostURL, {
  videoOriginalIndexForWeb: appConfig.videoOriginalIndexForWeb,
  audioOriginalIndexForWeb: appConfig.audioOriginalIndexForWeb,
});

export const addSURL = (surl) => {
  // hack to add the surl to the PictionJS instance
  piction.session._surl = surl; // eslint-disable-line no-underscore-dangle
};

export const addToken = (token) => {
  // hack to add the token to the PictionJS instance
  piction.session._token = token; // eslint-disable-line no-underscore-dangle
};

export const setCookies = (val) => {
  piction.session._isSurlCookie = val; // eslint-disable-line no-underscore-dangle
};

/**
 * Piction returns a language index on login/validate
 * need to transform it for kaui2 standard
 * @param langIndex
 * @param languageList
 */
export const getLanguageFromPictionIndex = (
  langIndex,
  languageList,
) => (languageList && languageList.find((lang) => lang.$.idx === langIndex)) || 'ENGLISH';

export const getLocaleFromPictionIndex = (langIndx, languageList) => {
  const lang = getLanguageFromPictionIndex(langIndx, languageList);
  let locale = 'en';
  switch (lang._) {
    case 'ENGLISH': break;
    case 'FRENCH': locale = 'fr'; break;
    case 'GERMAN': locale = 'de'; break;
    // case 'ARABIC': locale = 'ar'; break;
    // case 'HINDI': locale = 'hi'; break;
    // case 'CHINESE': locale = 'zh'; break;
    // case 'SPANISH': locale = 'es'; break;
    // case 'RUSSIAN': locale = 'ru'; break;
    // case 'HEBREW': locale = 'he'; break;
    default:
      console.warn(
        `Locale ${locale} not recognised while trying to set language for piction calls`,
      );
      break;
  }
  return locale;
};

/**
 * Piction accepts a specific value for language rather than a locale code, so
 * this function deals with that.
 * @param locale
 */
export const getLanguageFromLocale = (locale) => {
  let language = '';

  switch (locale) {
    case 'en': break;
    case 'fr': language = 'FRENCH'; break;
    case 'de': language = 'GERMAN'; break;
    case 'ar': language = 'ARABIC'; break;
    case 'hi': language = 'HINDI'; break;
    case 'zh': language = 'CHINESE'; break;
    case 'es': language = 'SPANISH'; break;
    case 'ru': language = 'RUSSIAN'; break;
    case 'he': language = 'HEBREW'; break;
    default:
      console.warn(
        `Locale ${language} not recognised while trying to set language for piction calls`,
      );
      break;
  }

  return language;
};

/**
 * Mimic Piction return value for language selection
 * @param locale
 * @param languageList - list of availabl languages in DB
 */
export const getDataLanguageFromLocale = (locale, languageList) => {
  let langName = 'ENGLISH';

  switch (locale) {
    case 'en': break;
    case 'fr': langName = 'ENGLISH'; break;
    case 'de': langName = 'GERMAN'; break;
    case 'ar': langName = 'ARABIC'; break;
    case 'hi': langName = 'HINDI'; break;
    case 'zh': langName = 'CHINESE'; break;
    case 'es': langName = 'SPANISH'; break;
    case 'ru': langName = 'RUSSIAN'; break;
    case 'he': langName = 'HEBREW'; break;
    default:
      console.warn(
        `Locale ${langName} not recognised while trying to set language for piction calls`,
      );
      break;
  }

  const language =
    languageList ? languageList.filter(l => l._ === langName)[0] : { _: langName, $: { display: langName } };
  return language;
};

export const setDBLanguage = (lang) => {
  piction.setLanguage(lang);
};

export const setDataLanguage = (language) => {
  piction.setLanguage(language._);
};

/**
 * Provides easier way to store and retrieve the isWorkbench bool from session store. It shouldn't
 * change after initial load plus it is used throughout the app so easier than passing down the
 * value or mapping state to props for components.
 * @param isWorkbenchBool Optional. If set will set the state. If not, then will return current
 * state.
 */
export const isWorkbench = (isWorkbenchBool) => {
  if (isWorkbenchBool === false || isWorkbenchBool === true) {
    isWorkbenchValue = isWorkbenchBool;
  }
  return isWorkbenchValue;
};

/*
 * Required to check if categories/lightboxes should be restricted or not
 * workspaces are always editable if are visible
 * highlights are only editable to the creator, and never to discoverer users
 * @param category- the category to check permissions on
* */
export const isEditable = (category) => {
  // if category of type lightbox(workspace)/set always allow editing
  if (category.get('t') === 'L' || category.get('t') === 'S') {
    return !(category.get('wstype') === 'SET' && !isWorkbench());
  }
  // if restricted editing, work on roles,
  // or if collection editing enabled- return true for all collections
  if (appConfig.restrictEditing) {
    return (category.get('rw') === 'TRUE' || (category.get('t') === 'CL' && appConfig.allowCollectionEdit));
  }

  return true; // Always return true if the appConfig is not on
};

/**
 * Provides easier way to store and retrieve whether edit mode is enabled bool from session store.
 * Edit mode allows discoverer users more edit possibilities
 * (e.g edit the primary picture, and use crop or resize in the umoMenu)
 * It shouldn't change after initial load plus it is used throughout the app so easier than
 * passing down the value or mapping state to props for components.
 */
export const isEditMode = () => (isWorkbench() || appConfig.discovererEditMode);

export const createTemplateURL = (templateName, params, locale) => {
  let paramsQueryString = params ? `&${queryString.stringify(params)}` : '';

  if (locale && getLanguageFromLocale(locale)) {
    paramsQueryString += `&LANGUAGE=${getLanguageFromLocale(locale)}`;
  }
  return `${appConfig.hostURL}!tpl.private_page?&acid=&name=${templateName}${paramsQueryString}`;
};

export default piction;

export const retrieveAndApplyUserConfig = (surl, csrf, dispatch) => {
  if (appConfig.useUserDefinedConfig && csrf) {
    return $.post(`${appConfig.hostURL}!soap.ajaxget?n=KAUI_CONF`, { surl: surl || '', csrf })
      .done((res) => {
        try {
          (() => {
            if (res.contentType === 'text/xml' && res.childNodes[0]?.textContent.includes('SURL failed')) {
              dispatch(sessionExpired(res, true));
              return res;
            }
            else {
              if (res.indexOf('appConfig') < 0) {
                console
                  .warn('remote appConfig does not look like it has been defined correctly. '
                    + 'if remote appConfig is not required, please disable it in the main appConfig '
                    + 'to prevent this message from showing. '
                    + 'if remote appConfig is required, check that the config is defined with '
                    + '"appConfig = {...".');
              }
              const dat = _.split(res, '-->')[1]; // remove first line
              const userAppConfig = eval(dat); // eslint-disable-line no-eval
              updateConfig({ ...userAppConfig }); // eslint-disable-line no-undef
              piction.setDerivatives({
                vidDeriv: userAppConfig.videoOriginalIndexForWeb,
                audDeriv: userAppConfig.audioOriginalIndexForWeb,
              });
              dispatch(updateStoreFromNewConfig());
              if (userAppConfig.useCustomKAUI2Conf) {
                sessionStorage.setItem(LOCAL_STORAGE_KAUI2CONF, appConfig.useCustomKAUI2Conf);
                // const head = document.getElementsByTagName('head')[0];

                // // Creating link element
                // const customStyle = document.createElement('link');
                // customStyle.href = `../${userAppConfig.useCustomKAUI2Conf}/css/custom.css`;
                // customStyle.type = 'text/css';
                // customStyle.rel = 'stylesheet';
                // head.append(customStyle);

                // const customJS = document.createElement('script');
                // customJS.src = `../${userAppConfig.useCustomKAUI2Conf}/custom.js`;
                // customJS.type = 'text/javascript';
                // head.append(customJS);
                // // embed css and custom.js files from new kaui2conf
                embedExternalScript(`../${userAppConfig.useCustomKAUI2Conf}/css/custom.css`, true, false);
                embedExternalScript(`../${userAppConfig.useCustomKAUI2Conf}/custom.js`, false, false);
              }
              if (appConfig.menuOptions.workspaceMenu.includes('saveToDropbox')) { // load dropbox script only if working with dropbox
                embedExternalScript('https://www.dropbox.com/static/api/2/dropins.js', false, true, 'dropboxjs', { appKey : 'vwvdpdxi1pgh0xi' });
              }
            }
          })();
        } catch (err) {
          throw new Error('unable to parse the user defined config', err);
        }
      })
      .fail((res) => {
        if (res.responseText.indexOf('<!--' < 0)) console.warn(res);
        else {
          const dat = _.split(res, '-->')[1]; // remove first line
          const userAppConfig = eval(dat); // eslint-disable-line no-eval
          updateConfig({ ...userAppConfig }); // eslint-disable-line no-undef
          piction.setDerivatives({
            vidDeriv: userAppConfig.videoOriginalIndexForWeb,
            audDeriv: userAppConfig.audioOriginalIndexForWeb,
          });
          dispatch(updateStoreFromNewConfig());
        }
      });
  } return null;
};

export const metaAttach = (meta, umoIDList, isReplace, intl, searchQuery) => {
  const repeatingFieldTypes = ['RGROUP'];
  const options = [];

  const repeatFields = meta.filter((tag) => repeatingFieldTypes.includes(tag.type) || tag.repeatingField === true);

  // special param for multi lingual to set an empty value for the default language (i.e english)
  // in case we only create a value in another language (i.e italian)- we want the original (and all others)
  // to not display the value in italian
  if (appConfig.enableMultiLang) {
    options.push(['MULTILINGUAL_INITIALISE', 'TRUE']);
  }

  // If not appending, then clear out repeating fields.
  if (isReplace) {
    for (const tag in repeatFields) {
      // Handle rgroup children instead of parent
      if (repeatingFieldTypes.includes(repeatFields[tag].type)) {
        const rTags = repeatFields[tag].rgroup;
        for (let i = 0; i < rTags.length; i += 1) {
          options.push(['META_TAG', rTags[i].name]);
          options.push(['DELETE', 'TRUE']);
        }
      } else {
        options.push(['META_TAG', repeatFields[tag].name]);
        options.push(['DELETE', 'TRUE']);
      }
    }
  }

  for (const tag in meta) {
    if (repeatingFieldTypes.includes(meta[tag].type)) { // repeating group
      const rTags = {};
      // Parse out repeating group values
      for (let k = 0; k < meta[tag].value.length; k += 1) {
        const value = meta[tag].value[k];
        for (let i = 0; i < value.length; i += 1) {
          if (!rTags[value[i].name]) rTags[value[i].name] = [];
          rTags[value[i].name].push(value[i].value || '');
        }
      }

      for (const rTag in rTags) {
        for (let k = 0; k < rTags[rTag].length; k += 1) {
          options.push(['META_TAG', rTag]);

          if (!isReplace) options.push(['POSITION_ID', 'APPEND']);
          else options.push(['POSITION_ID', k + 1]);

          options.push(['VALUE', encodeURIComponent(rTags[rTag][k] || '')]);
        }
      }
    } else if (meta[tag].repeatingField) { // repeating single field
      for (let k = 0; k < meta[tag].value.length; k += 1) {
        options.push(['META_TAG', meta[tag].name]);

        if (!isReplace) options.push(['POSITION_ID', 'APPEND']);
        else options.push(['POSITION_ID', (k + 1)]);

        options.push(['VALUE', encodeURIComponent(meta[tag].value[k].value || '')]);
      }
    } else {
      options.push(['META_TAG', meta[tag].name]);

      if (isReplace || meta[tag].isReplace) {
        options.push(['VALUE', encodeURIComponent(meta[tag].value || '')]);
      } else {
        // for appending keywords UMO_INSERT fields (used in media populate)- add comma before new values
        if (meta[tag].valueType && ['KEYWORD','UMO_INSERT'].includes(_.toUpper(meta[tag].valueType))) options.push(['SUFFIX_VALUE', `,${encodeURIComponent(meta[tag].value || '')}`]);
        else options.push(['SUFFIX_VALUE', encodeURIComponent(meta[tag].value || '')]);
      }
    }
  }

  // if search query exists- use that, and exclude umos on the list ("deselected" umos)
  const umoArray = _.isArray(umoIDList) ? umoIDList : [umoIDList];
  if (searchQuery) {
    options.push(['SEARCH', searchQuery]);
    for (let i = 0; i < umoArray.length; i += 1) {
      options.push(['EXCLUDE_UMO_ID', umoArray[i]]);
    }
  } else {
    // Insert all umos into webservice call
    for (let i = 0; i < umoArray.length; i += 1) {
      options.push(['UMO_ID', umoArray[i]]);
    }
  }

  return piction.request('Metadata_Update_V2', options).then((res) => {
    for (let i = 0; i < res.a.RETURN_MESSAGE.length; i += 1) {
      if (res.a.RETURN_MESSAGE[i]._ !== 'Updated') {
        if (res.a.RETURN_MESSAGE.length > 1) {
          throw new Error(
            intl ? intl.formatMessage({ id: 'metaAttach.umosExpectedError' })
              : 'There was at least one error while performing metadata attach. Please try again.',
          );
        }
        throw new Error(res.a.RETURN_MESSAGE[i]);
      }
    }
    if (res.message && res.message.includes(SESSION_EXPIRED_MESSAGE)) {
      throw new Error(res);
    }

    return { success: true, res };
  });
};

export const emailUMOsViaSearchQuery = (lightboxID, searchQuery, fromName, fromEmail, toEmail, message, isPortal, options) => {
  let callArgs;
  let reqType = 'email_image';

  if (isPortal) {
    reqType = 'mng_portal';
    callArgs = {
      SEARCH: searchQuery,
      ACTION: 'CREATE_WORKSPACE',
      USERNAME: fromName,
      PASSWORD: toEmail,
      EMAIL_ADDRESS: toEmail,
      MESSAGE: message,
      WORKSPACE: lightboxID,
      TEMPLATE: appConfig.portalShareEmail || 'KAUI2.Portal_Email',
      ...options,
    };
    if (appConfig.portalShareUsername) {
      callArgs.BASE_ACCOUNT_ON_USERNAME = appConfig.portalShareUsername;
    }
  } else {
    callArgs = {
      SEARCH: searchQuery,
      DESC: fromName,
      FROM_EMAIL: fromEmail,
      TO_EMAIL: toEmail,
      MESSAGE: message,
      DOWNLOAD_WHAT: 'WEB',
      EMAIL_TEMPLATE: 'download_image.html',
      ...options,
    };
  }

  return piction.request(reqType, callArgs).then((res) => {
    if (res && res.a && res.a.RETURN_MESSAGE && _.isArray(res.a.RETURN_MESSAGE)) {
      if (res.a.RETURN_MESSAGE[0] === 'Email Sent') {
        return { success: true };
      }

      throw new Error(res.a.RETURN_MESSAGE[0]);
    } else if (res.a.SUCCESS && _.isArray(res.a.SUCCESS)) {
      if (_.includes(res.a.SUCCESS[0]._, 'Acct created')) {
        return { success: true };
      }
    } else {
      if (res.message && res.message.includes(SESSION_EXPIRED_MESSAGE)) {
        throw new Error(res);
      }
      throw new Error('Invalid response');
    }
  });
};

export const emailUMOsViaLightbox = (lightboxID, fromName, fromEmail, toEmail, message, isPortal) =>
  emailUMOsViaSearchQuery(
    lightboxID,
    `IN_CATEGORY:"${lightboxID}"`,
    fromName,
    fromEmail,
    toEmail,
    message,
    isPortal,
  );

export const relateUMOsPeer = (options) => {
  const callArgs = { ...options };

  return piction.request('RELATE_BY', callArgs).then((res) => {
    if (res && res.RETURN_MESSAGE) {
      return res.RETURN_MESSAGE;

      throw new Error(res.RETURN_MESSAGE);
    }
    if (res.message && res.message.includes(SESSION_EXPIRED_MESSAGE)) {
      throw new Error(res);
    } else {
      throw new Error('Invalid response');
    }
  });
};

export const rateUMO = (options) => {
  const callArgs = {
    UPDATE_INCLUDE_SUMMARY: 'TRUE',
    INCLUDE_SUMMARY: 'TRUE',
    ...options,
  };

  return piction.request('NWS_RATING', callArgs).then((res) => {
    if (res && res.RETURN_MESSAGE) {
      return res.RETURN_MESSAGE;

      throw new Error(res.RETURN_MESSAGE);
    }
    if (res.message && res.message.includes(SESSION_EXPIRED_MESSAGE)) {
      throw new Error(res);
    } else {
      throw new Error('Invalid response');
    }
  });
};

export const getUserList = (options) => {
  const callArgs = {
    SORT: 'USERNAME',
    FULLNAME: 'TRUE',
    ...options,
  };

  return piction.request('Profile', callArgs).then((res) => {
    if (res && res.a && res.a.p && _.isArray(res.a.p)) {
      return res.a.p;

      throw new Error(res.a);
    }
    if (res.message && res.message.includes(SESSION_EXPIRED_MESSAGE)) {
      throw new Error(res);
    } else {
      throw new Error('Invalid response');
    }
  });
};

export const moveCategory = (categoryIDToMove, destinationCategoryID, destinationCollectionID) => {
  const callArgs = {
    FROM_COLLECTION: 0,
    TO_COLLECTION: destinationCollectionID,
    CURRENT_CATEGORY_ID: categoryIDToMove,
    PARENT_NEW_CATEGORY_ID: destinationCategoryID,
    ENFORCE_CHECKS: appConfig.restrictEditing ? 'CHECK_TO_CTG' : 'NONE',
    ACTION: 'MOVE_FOLDER',
  };

  return piction.request('UTILITY', callArgs).then((res) => {
    let returnMessage;

    try {
      if (res.a.RESULT && _.isArray(res.a.RESULT)) {
        returnMessage = res.a.RESULT[0] === 'MOVED' ? 'MOVED' : 'utils.piction.moveCategoryGenericError';
      } else {
        // Hack to work with different error types returning from server
        if (res.a.RETURN_MESSAGE[0]._ &&
          ['Session does not have insert, update or alter on target category',
            'Session does not have insert, update or alter on role in target collection as is root level category']
            .includes(res.a.RETURN_MESSAGE[0]._)) {
          returnMessage = 'utils.piction.moveCategoryPermissionError';
        } else {
          returnMessage = res.a.RETURN_MESSAGE[0] === 'MOVED' ? 'MOVED' : 'utils.piction.moveCategoryGenericError';
        }
      }
    } catch (err) {
      if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
        returnMessage = 'general.sessionExpired';
      } else {
        returnMessage = 'utils.piction.moveCategoryGenericError';
      }
    }

    if (returnMessage === 'MOVED') {
      return undefined;
    }

    throw new Error(returnMessage);
  });
};

export const rotateImage = (umoID, options, dispatch) => {
  const callArgs = { UMO_ID: umoID, ...options };
      
  return piction.request('NWS_IMAGE_PROCESS', callArgs, { useJSON: true }).then((res) => {
    if (res && res.results?.length > 0 && res.results[0].result === 'success') {
      dispatch(addIntlInfo('imageEdit.rotateImageSuccess'));
      return res.results[0];
    }
    if (res.message && res.message.includes(SESSION_EXPIRED_MESSAGE)) {
      dispatch(sessionExpired(res));
      return res;
    } else {
      dispatch(addIntlError('utils.piction.moveCategoryGenericError'));
      console.log('rotate error: ', res);
      return res;
    }
    return res;
  }).catch((err) => {
    if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
      dispatch(sessionExpired(err));
      return err;
    } else {
      dispatch(addIntlError('utils.piction.moveCategoryGenericError'));
      console.error('rotate error: ', err);
      return err;
    }
  });
};

/**
 * When retrieving categories from Piction we usually return with only a depth of 1 so that it's
 * faster. What this utility does is merge child category query data back in to the original
 * category query.
 *
 * For example we do a CategoriesXML category retrieval for the root categories which returns
 * something like this:
 *
 * [
 * { i: '001', n: 'root cat 1', ct: [], ch: 'FALSE' },
 * { i: '002', n: 'root cat 2', ct: [], ch: 'TRUE' },
 * ]
 *
 * We then want to retrieve the children for category 002 which returns this:
 *
 * [
 * { i: '0021', n: 'cat 2 1', ct: [], ch: 'FALSE' },
 * { i: '0022', n: 'cat 2 2', ct: [], ch: 'FALSE' },
 * ]
 *
 * This utility will merge the children data with the original (root level) category data:
 *
 * [
 * { i: '001', n: 'root cat 1', ct: [], ch: 'FALSE' },
 * { i: '002', n: 'root cat 2', ct: [{ i: '0021', ... }, { i: '0022', ... }], ch: 'TRUE' },
 * ]
 *
 * See the tests for practical use.
 *
 * @param categoryList This is essentially the category response from piction where the children
 * will be inserted to
 * @param categoryIDToUpdate Specific category ID that the children should be inserted to
 * @param updatedChildrenList Array of children to insert
 */
// todo: this could probably be used for the category/workspace retrieval (see respective reducers)
export const updateCategoryChildren = (categoryList, categoryIDToUpdate, updatedChildrenList) => {
  const keyPath = getKeyPath(categoryList, 'i', categoryIDToUpdate, 'ct');

  if (keyPath) {
    // create a lookup of the current children so they can be easily found
    const currentChildrenLookup = {};
    categoryList.getIn([...keyPath, 'ct']).forEach((item, index) => {
      currentChildrenLookup[item.get('i')] = index;
    });

    // go through the updated children list and copy over its previously returned children, if any.
    const newChildrenList = fromJS(updatedChildrenList).map((item) => {
      if (Object.prototype.hasOwnProperty.call(currentChildrenLookup, item.get('i'))) {
        const currentChildIndex = currentChildrenLookup[item.get('i')];
        return item.set('ct', categoryList.getIn([...keyPath, 'ct', currentChildIndex, 'ct']));
      }
      return item;
    });

    return categoryList.setIn(
      [...keyPath, 'ct'],
      newChildrenList,
    );
  }
  return categoryList;
};

export const getUMOShareLink = (dispatch, umoID, options) => {
  const callArgs = { ...options };

  return piction.getUMOs(`UMO_ID:"${umoID}"`, callArgs).then((res) => {
    if (res.res.a.r) return res.umoList.all[0];
  })
    .catch((err) => {
      if (err.message && (err.message.includes(SESSION_EXPIRED_MESSAGE)
        || err.message.includes('You are not logged in'))) {
        dispatch(sessionExpired(err));
      } else {
        dispatch(retrieveUMOsFailure(err));
      }
      return err;
    });
};

export const sendUsageData = (umoID, data, dispatch) => {
  const callArgs = { UMO_ID: umoID, ...data };

  return piction.request('DATA_IN_BASKET', callArgs)
    .then((res) => {
      console.log('data usage', res);
      return res;
    })
    .catch((err) => {
      if (err.message && (err.message.includes(SESSION_EXPIRED_MESSAGE)
        || err.message.includes('You are not logged in'))) {
        dispatch(sessionExpired(err));
      } else {
        dispatch(retrieveUMOsFailure(err));
      }
      return err;
    });
};

export const registerUserCode = (dispatch) => {
  const surl = sessionStorage.getItem(LOCAL_STORAGE_SURL_KEY);
  return piction.request('REGISTER_2FA', { action: 'REGISTER' }, { surl })
    .then((res) => res)
    .catch((err) => {
      if (err.message && (err.message.includes(SESSION_EXPIRED_MESSAGE)
        || err.message.includes('You are not logged in'))) {
        dispatch(sessionExpired(err));
      }
      return err;
    });
};

export const resetEnvironmentParams = () => {
  // reset the environment
  // login through this function is done via kaui2
  // and not external url- so remove session storage param
  addSURL(null);
  setCookies(null);
  sessionStorage.removeItem(LOCAL_STORAGE_EXT_URL);
  sessionStorage.removeItem(LOCAL_STORAGE_KAUI2CONF);
  piction.setLanguage(undefined);
};

export const getLOVListItems = (value, listID) => {
  if (!listID || !value || value.length < 1) return [];
  const options = {
    OPTION: 'AUTOSUGGEST_CCLIST',
    CLIST_PK: listID,
    SEARCH: value.length === 1 ? `${value}%` : `%${value}%`,
  };  
  return piction.request('THESAURUS_SEARCH', options)
    .then((res) => {
      if (res?.a?.rs) {
        const list = _.isArray(res.a.rs) ? res.a.rs : [res.a.rs];
        return _.map(list, val => ({ value: val._, label: val._, id: val.$.id }));
      }
      return [];
    }).catch((err) => {
      console.error('Error getting LOV list items', err);
      return [];
    });
};